import React, { useEffect, useState } from 'react';
import { Checkbox, TextField } from '@mui/material';
import Text from '../text';
import REGEX_CONSTANTS from 'utils/constants/regex';
import ApiService from 'utils/services/ApiService';
import { deleteUserData } from 'utils/services/idbService';
import { addEventLog } from 'utils/functions/eventLogFunctions';

interface ShareWithNewUserProps {
	siteId: number;
	setShowNewUser: React.Dispatch<React.SetStateAction<boolean>>;
	handleSiteOwner: (users: any, userEmail: string) => void;
	users: any;
	setUsers: React.Dispatch<React.SetStateAction<any>>;
	sendPopUpMessage: (type: string, message: string) => void;
}

const ShareWithNewUser: React.FC<ShareWithNewUserProps> = ({
	siteId,
	setShowNewUser,
	handleSiteOwner,
	setUsers,
	users,
	sendPopUpMessage,
}) => {
	const [addUserLoading, setAddUserLoading] = useState(false);
	const loggedUser = JSON.parse(
		localStorage.getItem('user_information') || '{}'
	);

	const [newInviteEmail, setNewInviteEmail] = useState('');
	const [emailError, setEmailError] = useState('');
	const [isNewUserOwner, setIsNewUserOwner] = useState(false);

	const resetErrors = () => {
		setEmailError('');
		setNewInviteEmail('');
	};

	const validateEmail = (email: string) => {
		if (email === '') return setEmailError('User email cannot be empty');
		if (!REGEX_CONSTANTS.EMAIL_REGEX.test(email))
			return setEmailError('Invalid email format');
		return true;
	};

	const handleNewInviteEmail = (e: React.ChangeEvent<HTMLInputElement>) => {
		resetErrors();
		setNewInviteEmail(e.target.value.trim());
	};

	const checkUserEmail = async (email: string) => {
		let userFound = null;
		try {
			const result = await ApiService.checkUserEmail(email);
			if (result.status !== 200)
				throw Error('Error connecting with the server.');
			userFound = result.data ? result.data.id : null;
		} catch (error) {}
		return userFound;
	};

	const isUserAlreadyInTheSite = async (userEmail: string) => {
		let result = false;
		await users.forEach((user: any) => {
			if (user.email === userEmail) {
				setEmailError('This user already has access to this site.');
				result = true;
			}
		});
		return result;
	};

	const shareSiteWithANewUser = async (
		sharedwith: any,
		userEmail: string
	) => {
		const newUser = [
			{
				sharedwith: sharedwith,
				sharedOn: new Date().toISOString(),
				sharedBy: loggedUser.id,
				status: 'Active',
				siteId: siteId,
				sharedwithEmail: userEmail,
			},
		];
		let result;
		try {
			result = await ApiService.shareSite(newUser, newInviteEmail, siteId);
			if (result.status !== 200)
				throw Error('Error connecting with the server.');
			setShowNewUser(false);
			resetErrors();
			sendPopUpMessage('Success', 'Invite sent to ' + newInviteEmail);
			setUsers(result.data);
			setAddUserLoading(false);
      		await deleteUserData(userEmail, siteId);
			if (isNewUserOwner) handleSiteOwner(result.data, userEmail);

		} catch (error) {
			console.log(error);
			sendPopUpMessage(
				'error',
				'Error sending the invite to ' + newInviteEmail
			);
			setAddUserLoading(false);
			// The error wasn't during the call to share the site, but some subsequent call.
			if(result && result.status === 200) {
				addEventLog("Share Site Failed", "Site Management", siteId, '', `Tried to share with ${newInviteEmail}, Error: ${error}`);
			}
		}
	};

	const handleSendNewInvite = async () => {
		if (!validateEmail(newInviteEmail)) return false;
		if (await isUserAlreadyInTheSite(newInviteEmail)) return false;
		setAddUserLoading(true);
		const sharedwith = await checkUserEmail(newInviteEmail);
		await shareSiteWithANewUser(sharedwith, newInviteEmail);
	};

	const handleNewUserCheckbox = async (
		e: React.ChangeEvent<HTMLInputElement>
	) => {
		setIsNewUserOwner(e.target.checked);
	};

	useEffect(() => {}, [newInviteEmail]);

	return (
		<div
			className="
              grid grid-cols-12
              font-jost font-[400] text-[16px] tracking-[0.15px] leading-[24px] text-[#00000099]
              border-b-[1px] border-[#E6E6E6] 
            "
		>
			<div className="col-span-1">
				<Checkbox
					sx={{
						color: '#D8D8D8',
						'& .MuiSvgIcon-root': { fontSize: 24 },
					}}
					checked={isNewUserOwner}
					onChange={handleNewUserCheckbox}
				/>
			</div>
			<div className="col-span-4 self-center flex flex-row gap-[10px] items-center bg-[#F4F8F9] h-full pl-[12px]">
				--
			</div>
			<div className="col-span-4 self-center relative">
				<TextField
					value={newInviteEmail}
					size="small"
					style={{
						width: 384,
						position: 'absolute',
						top: -20,
					}}
					InputProps={{
						style: {
							borderRadius: '0px',
						},
					}}
					onChange={handleNewInviteEmail}
					error={emailError !== ''}
					helperText={emailError}
					label="User email"
				/>
			</div>
			<div className="col-span-3 self-center flex flex-row justify-between pl-[60px]">
				{!addUserLoading ? (
					<button
						type="button"
						className="
                  text-[#0092C8] px-[10px] py-[5px] rounded-[4px] ml-[16px]
                  hover:shadow-sm hover:bg-[#0092c80a] 
                  focus:shadow-sm focus:bg-[#0092c80a] focus:outline-none
                "
						onClick={handleSendNewInvite}
					>
						Send Invite
					</button>
				) : (
					<Text
						addStyle="
                    !font-inter !text-[14px] !mt-2 !mr-2 !text-primary-lu-blue 
                    !font-semibold justify-center cursor-progress
                  "
					>
						Saving...
					</Text>
				)}
				<button
					className="
                  !h-[40px] w-[40px] flex justify-center items-center 
                  px-[10px] py-[5px] rounded-[4px]
                  hover:shadow-sm hover:bg-[#0092c80a] 
                  focus:shadow-sm focus:bg-[#0092c80a] focus:outline-none
                "
					disabled={addUserLoading}
					onClick={() => {
						setShowNewUser(false);
						resetErrors();
					}}
				>
					<img
						src="/assets/icons/Trash.svg"
						alt="Drawer Icon"
						className="w-5 h-5 md:block hidden"
					/>
				</button>
			</div>
		</div>
	);
};

export default ShareWithNewUser;
